import { create } from 'zustand'

import { CLIENT_ID } from '@/api/client'
import {
  bulkRetryDownloadJobs,
  deleteDownloadJob,
  getDownloadJobs,
  retryDownloadJob,
  setDownloadJobPriority,
  submitDownloadUrl,
} from '@/api/services/download-queue'
import { registerSyncListener } from '@/api/sync-events'
import type { DownloadJobSummary } from '@/api/types/download-queue'
import { parseUtcDate } from '@/lib/date-utils'

type DownloadQueueState = {
  jobs: DownloadJobSummary[]
  error: string | null
  pollingActive: boolean
}

type DownloadQueueActions = {
  fetchJobs: () => Promise<void>
  submitUrl: (url: string) => Promise<DownloadJobSummary>
  retry: (jobId: number) => Promise<void>
  retryAll: () => Promise<number>
  remove: (jobId: number) => Promise<void>
  prioritize: (jobId: number, priority: number) => Promise<void>
  startPolling: () => void
  stopPolling: () => void
}

const POLL_INTERVAL_MS = 5_000

// setTimeout ID をストア外で管理（Zustand stateに入れると毎回再レンダーを起こす）
let timerId: ReturnType<typeof setTimeout> | null = null
// 前回サーバーから受け取った server_time（差分取得用）
let lastServerTime: string | null = null

function scheduleNext() {
  const { pollingActive, fetchJobs } = useDownloadQueueStore.getState()
  if (!pollingActive) return

  timerId = setTimeout(async () => {
    if (document.hidden) {
      scheduleNext()
      return
    }
    await fetchJobs()
    scheduleNext()
  }, POLL_INTERVAL_MS)
}

function clearTimer() {
  if (timerId !== null) {
    clearTimeout(timerId)
    timerId = null
  }
}

/** ジョブ配列が実質的に同じかどうかを比較（同じなら再レンダーをスキップ） */
function jobsEqual(a: DownloadJobSummary[], b: DownloadJobSummary[]): boolean {
  if (a.length !== b.length) return false
  for (let i = 0; i < a.length; i++) {
    const aj = a[i]
    const bj = b[i]
    if (
      aj.id !== bj.id ||
      aj.status !== bj.status ||
      aj.progress !== bj.progress ||
      aj.message !== bj.message ||
      aj.has_error !== bj.has_error ||
      aj.completed_at !== bj.completed_at ||
      aj.priority !== bj.priority
    ) {
      return false
    }
  }
  return true
}

/**
 * 差分レスポンスを既存のジョブリストにマージする。
 * - changed: 更新/新規ジョブ
 * - allIds: 現在有効な全ID（これに含まれないローカルジョブは削除済み）
 */
function mergeJobs(
  current: DownloadJobSummary[],
  changed: DownloadJobSummary[],
  allIds: Set<number>,
): DownloadJobSummary[] {
  // changed を Map 化
  const changedMap = new Map(changed.map((j) => [j.id, j]))

  // 既存ジョブを更新 or 維持（allIds に無いものは除外）
  const merged: DownloadJobSummary[] = []
  for (const job of current) {
    if (!allIds.has(job.id)) continue // 削除された
    merged.push(changedMap.get(job.id) ?? job)
    changedMap.delete(job.id)
  }

  // 新規ジョブを追加
  for (const job of changedMap.values()) {
    merged.push(job)
  }

  // created_at desc でソート
  merged.sort((a, b) => parseUtcDate(b.created_at).getTime() - parseUtcDate(a.created_at).getTime())
  return merged
}

export const useDownloadQueueStore = create<DownloadQueueState & DownloadQueueActions>((set, get) => ({
  jobs: [],
  error: null,
  pollingActive: false,

  fetchJobs: async () => {
    try {
      const res = await getDownloadJobs(lastServerTime ?? undefined)
      const prev = get().jobs

      let nextJobs: DownloadJobSummary[]
      if (lastServerTime === null) {
        // 初回ロード: 全データ
        nextJobs = res.jobs
      } else {
        // 差分マージ
        nextJobs = mergeJobs(prev, res.jobs, new Set(res.all_ids))
      }

      lastServerTime = res.server_time

      if (jobsEqual(prev, nextJobs)) {
        if (get().error !== null) set({ error: null })
        return
      }
      set({ jobs: nextJobs, error: null })
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'ジョブ一覧の取得に失敗しました'
      set({ error: msg })
    }
  },

  submitUrl: async (url: string) => {
    const job = await submitDownloadUrl(url)
    // 全データ再取得（新規追加時はリセット）
    lastServerTime = null
    await get().fetchJobs()
    return job
  },

  retry: async (jobId: number) => {
    await retryDownloadJob(jobId)
    lastServerTime = null
    await get().fetchJobs()
  },

  retryAll: async () => {
    const { retried } = await bulkRetryDownloadJobs()
    lastServerTime = null
    await get().fetchJobs()
    return retried
  },

  remove: async (jobId: number) => {
    await deleteDownloadJob(jobId)
    set((state) => ({ jobs: state.jobs.filter((j) => j.id !== jobId) }))
  },

  prioritize: async (jobId: number, priority: number) => {
    await setDownloadJobPriority(jobId, priority)
    set((state) => ({
      jobs: state.jobs.map((j) => (j.id === jobId ? { ...j, priority } : j)),
    }))
  },

  startPolling: () => {
    if (get().pollingActive) return
    set({ pollingActive: true })
    get().fetchJobs()
    scheduleNext()
  },

  stopPolling: () => {
    set({ pollingActive: false })
    clearTimer()
  },
}))

// ============================================================================
// SSE リモートイベント購読
// ============================================================================
// 他端末でキューが変更されたら、ページを開いている (polling active) 場合のみ再取得する。
// ページ非表示中は既存の polling 側 (document.hidden 判定) に任せる。
// 差分ポーリング用の lastServerTime は fetchJobs 内でそのまま使えるので、
// SSE トリガでも通常の差分取得ルートに乗る。
registerSyncListener((event) => {
  if (event.type !== 'download_queue.updated') return
  if (event.origin === CLIENT_ID) return

  const state = useDownloadQueueStore.getState()
  if (!state.pollingActive) return
  void state.fetchJobs()
})
